
import React, { useState, useEffect } from 'react';
import { TableColumn } from '../../types';

interface TableInputProps {
  columns: TableColumn[];
  value: Record<string, any>[];
  onChange: (value: Record<string, any>[]) => void;
  minRows?: number;
  maxRows?: number;
  className?: string;
}

export const TableInput: React.FC<TableInputProps> = ({
  columns,
  value = [],
  onChange,
  minRows = 1,
  maxRows,
  className = '',
}) => {
  const [data, setData] = useState<Record<string, any>[]>([]);

  // Initialize data when component mounts or when value prop changes
  useEffect(() => {
    const initializeData = () => {
      let initialData = [...value];
      
      // Ensure minimum rows
      while (initialData.length < minRows) {
        const newRow: Record<string, any> = {};
        columns.forEach(col => {
          newRow[col.id] = col.type === 'boolean' ? false : '';
        });
        initialData.push(newRow);
      }
      
      return initialData;
    };

    setData(initializeData());
  }, [value, columns, minRows]);

  const updateData = (newData: Record<string, any>[]) => {
    setData(newData);
    onChange(newData);
  };

  const addRow = () => {
    if (maxRows && data.length >= maxRows) return;
    
    const newRow: Record<string, any> = {};
    columns.forEach(col => {
      newRow[col.id] = col.type === 'boolean' ? false : '';
    });
    
    updateData([...data, newRow]);
  };

  const removeRow = (index: number) => {
    if (data.length <= minRows) return;
    
    const newData = data.filter((_, i) => i !== index);
    updateData(newData);
  };

  const updateCell = (rowIndex: number, columnId: string, cellValue: any) => {
    const newData = [...data];
    newData[rowIndex] = { ...newData[rowIndex], [columnId]: cellValue };
    updateData(newData);
  };

  const renderCell = (row: Record<string, any>, column: TableColumn, rowIndex: number) => {
    const value = row[column.id] || '';
    
    switch (column.type) {
      case 'boolean':
        return (
          <input
            type="checkbox"
            checked={value === true}
            onChange={(e) => updateCell(rowIndex, column.id, e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
        );
      
      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => updateCell(rowIndex, column.id, parseFloat(e.target.value) || 0)}
            className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={{ width: column.width || 'auto' }}
          />
        );
      
      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => updateCell(rowIndex, column.id, e.target.value)}
            className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={{ width: column.width || 'auto' }}
          />
        );
      
      default: // string
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => updateCell(rowIndex, column.id, e.target.value)}
            className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={{ width: column.width || 'auto' }}
          />
        );
    }
  };

  if (columns.length === 0) {
    return (
      <div className="p-4 border border-dashed border-gray-300 rounded-lg text-center text-gray-500">
        No columns configured for this table field.
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-gray-300">
          <thead>
            <tr className="bg-gray-50">
              {columns.map((column) => (
                <th
                  key={column.id}
                  className="border border-gray-300 px-3 py-2 text-left font-medium text-gray-700"
                  style={{ width: column.width || 'auto' }}
                >
                  {column.title}
                  {column.required && <span className="text-red-500 ml-1">*</span>}
                </th>
              ))}
              <th className="border border-gray-300 px-3 py-2 text-center font-medium text-gray-700 w-16">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-gray-50">
                {columns.map((column) => (
                  <td key={column.id} className="border border-gray-300 px-3 py-2">
                    {renderCell(row, column, rowIndex)}
                  </td>
                ))}
                <td className="border border-gray-300 px-3 py-2 text-center">
                  <button
                    type="button"
                    onClick={() => removeRow(rowIndex)}
                    disabled={data.length <= minRows}
                    className="text-red-600 hover:text-red-800 disabled:text-gray-400 disabled:cursor-not-allowed px-2 py-1 rounded"
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <div className="flex justify-between items-center">
        <button
          type="button"
          onClick={addRow}
          disabled={maxRows ? data.length >= maxRows : false}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          + Add Row
        </button>
        
        <span className="text-sm text-gray-600">
          {data.length} row{data.length !== 1 ? 's' : ''}
          {minRows && ` (min: ${minRows})`}
          {maxRows && ` (max: ${maxRows})`}
        </span>
      </div>
    </div>
  );
};
